/**
 * patrol-human-search-edit.js
 */

import React from 'react';
import {
  Box,
  useMediaQuery,
  Grid,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { parseISO, formatISO9075, isValid } from 'date-fns';
import useRecordStore from 'contexts/record-store';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { Types } from 'utils/constants';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import HiddenInput from 'components/form-inputs/hidden-input';
import DateTimePicker from 'components/form-inputs/date-time-picker';
import TextField from 'components/form-inputs/text-field';
import CheckboxList from 'components/form-inputs/checkbox-list';
import Autocomplete from 'components/form-inputs/autocomplete';
import Checkbox from 'components/form-inputs/checkbox';
import { PatrolTypeTitle } from '../../training-utils';


export default function PatrolHumanSearchEdit(props) {
  const {
    storedRecord,
    patrolRecord,
    patrolIndex,
    patrolTypes,
    exerciseId,
    drawerState,
    translation,
    isExerciseEditor,
    options,
    hidden,
    defaultMonitor,
    formPrefix = '',
  } = props;
  const formNameBase = `${formPrefix}patrol.patrolTypes[${patrolIndex}].humanSearch`;
  const typeRecord = patrolRecord?.humanSearch;
  const recordStore = useRecordStore();
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId);
  const upSm = useMediaQuery(theme => theme.breakpoints.up("sm"), { noSsr: true });
  const { setValue } = useFormContext();
  const { t } = useTranslation(translation);

  // initialize form values
  React.useEffect(() => {
    if (!isExerciseEditor && !storedRecord.dateCompleted) {
      // get exercise record to populate shared field values
      if (exerciseRecord.get()) {
        const exerPatrolRecord = exerciseRecord.get().patrol.patrolTypes[patrolIndex].humanSearch;
        setValue(`${formNameBase}.types`, exerPatrolRecord.types);
        setValue(`${formNameBase}.decoyCount`, exerPatrolRecord.decoyCount);
        setValue(`${formNameBase}.monitor`, exerciseRecord.get().monitor);
      }

      // set monitor to default value if provided
      if (defaultMonitor) {
        setValue(`${formNameBase}.monitor`, defaultMonitor)
      }
    }

    // exerciseRecord can't be in, and we don't want to run again if only defaultMonitor changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExerciseEditor, storedRecord.id, storedRecord.dateCompleted, patrolIndex, setValue]);


  const form = (
    <Box sx={{ display: hidden ? 'none' : undefined }}>
      {/* id */}
      <HiddenInput 
        name={`${formNameBase}.id`}
        defaultValue={typeRecord?.id || ''}
      />


      {/* monitor */}
      {!isExerciseEditor &&
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('humanSearch.monitor.helpTitle')}
        text={t('humanSearch.monitor.helpInfo')}
        style={{ marginTop: 8 }}
      >
        {focusHandlers =>
          <Grid container spacing={2}>
            <FormGridItem>
              <Autocomplete
                name={`${formNameBase}.monitor`}
                label={t('humanSearch.monitor.label')}
                options={options.monitors.map(o => o.name)}
                focusHandlers={focusHandlers}
                defaultValue={typeRecord?.monitor || ''}
                rules={{
                  required: hidden ? undefined : t('humanSearch.monitor.errMsgEmpty'),
                  validate: value => hidden ? true : value.trim() !== '' || t('humanSearch.monitor.errMsgEmpty'),
                  maxLength: {
                    value: 100,
                    message: t('humanSearch.monitor.errMsgLength')
                  }
                }}
              />
            </FormGridItem>
          </Grid>}
      </HelpTextItem>}


      {/* controlled negative */}
      {!isExerciseEditor &&
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('humanSearch.controlledNegative.helpTitle')}
        text={t('humanSearch.controlledNegative.helpInfo')}
      >
        <Checkbox
          name={`${formNameBase}.isControlledNegative`}
          label={t('humanSearch.controlledNegative.label')}
          defaultValue={typeRecord?.isControlledNegative}
        />
      </HelpTextItem>}


      {/* human search types */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('humanSearch.types.helpTitle')}
        text={t('humanSearch.types.helpInfo')}
      >
        {focusHandlers =>
          <CheckboxList
            name={`${formNameBase}.types`}
            label={t('humanSearch.types.label')}
            options={options.humanSearchTypes}
            customOther
            focusHandlers={focusHandlers}
            defaultValue={typeRecord?.types || []}
            rules={{
              validate: hidden ? undefined : val => (val && val.length > 0) || t('humanSearch.types.errMsgEmpty')
            }}
          />}
      </HelpTextItem>
      

      {/* decoy / quarry name */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('humanSearch.decoyCount.helpTitle')}
        text={t('humanSearch.decoyCount.helpInfo')}
        style={{ marginTop: 16 }}
      >
        {focusHandlers =>
          <Grid container spacing={2}>
            <FormGridItem xs={12} smd={6} smm={6} sm={6} md={4} lg={4}>  
              <TextField
                name={`${formNameBase}.decoyCount`}
                label={t('humanSearch.decoyCount.label')}
                focusHandlers={focusHandlers}
                defaultValue={typeRecord?.decoyCount || ''}
                type="number"
                rules={{
                  required: hidden ? undefined : t('humanSearch.decoyCount.errMsgEmpty'),
                  max: {
                    value: 999999999,
                    message: t('humanSearch.decoyCount.errMsgSize')
                  },
                  pattern: {
                    value: /[0-9]+/,
                    message: t('humanSearch.decoyCount.errMsgNumber')
                  },
                }}
              />
            </FormGridItem>
          </Grid>}
      </HelpTextItem>


      {/* time settings */}
      {!isExerciseEditor &&
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('humanSearch.times.helpTitle')}
        text={t('humanSearch.times.helpInfo')}
      >
        {focusHandlers =>
          <EditorAccordion
            highlight
            name={t('humanSearch.times.label')}
            summary={
              <FormSectionTitle>
                {'OPTIONAL: ' + t('humanSearch.times.label')}
              </FormSectionTitle>
            }
            detail={
              <Grid 
                container 
                spacing={2} 
                direction={upSm ? 'row' : 'column'}
                style={{ paddingBottom: 4 }}
              >

                {/* start time */}
                <FormGridItem sm={6}>
                  <DateTimePicker
                    variant='time'
                    name={`${formNameBase}.startTime`}
                    label={t('humanSearch.times.labelStart')}
                    focusHandlers={focusHandlers}
                    defaultValue={typeRecord?.startTime || parseISO(storedRecord.eventDate)}
                    clearable
                    setValueAs={date => {
                      if (date && isValid(date)) {
                        // combine record date with time input
                        const eventDate = parseISO(storedRecord.eventDate);
                        const combined = new Date(eventDate.getFullYear(), 
                                                  eventDate.getMonth(), 
                                                  eventDate.getDate(), 
                                                  date.getHours(), 
                                                  date.getMinutes());
                        return formatISO9075(combined); // no-timezone string
                      }
                      return date;
                    }} 
                    format='HH:mm'
                  />
                </FormGridItem>

                {/* end time */}
                <FormGridItem sm={6}>
                  <DateTimePicker
                    variant='time'
                    name={`${formNameBase}.endTime`}
                    label={t('humanSearch.times.labelEnd')}
                    focusHandlers={focusHandlers}
                    defaultValue={typeRecord?.endTime || parseISO(storedRecord.eventDate)}
                    clearable
                    setValueAs={date => {
                      if (date && isValid(date)) {
                        // combine record date with time input
                        const eventDate = parseISO(storedRecord.eventDate);
                        const combined = new Date(eventDate.getFullYear(), 
                                                  eventDate.getMonth(), 
                                                  eventDate.getDate(), 
                                                  date.getHours(), 
                                                  date.getMinutes());
                        return formatISO9075(combined); // no-timezone string
                      }
                      return date;
                    }} 
                    format='HH:mm'
                  />
                </FormGridItem>
                
              </Grid>
            }
          />}

      </HelpTextItem>}
    </Box>
  );


  return isExerciseEditor
    ? form
    : (
    <>
      <EditorIconSection
        iconName='patrol-human-search'
        iconMarginTop={0}
        helpProps={{
          translation,
          section: 'humanSearch',
          suffix: patrolTypes.length > 1 ? ` (Scenario)` : '',
        }}
      >
        {!shouldRenderMobileLayout() &&
        <PatrolTypeTitle 
          patrolType={patrolRecord.type} 
          patrolTypes={patrolTypes} 
          step={patrolRecord.order}
        />}
        {form}
      </EditorIconSection>
    </>
  );
}